import React, { useState, useRef, useEffect } from "react";
import Rating from "@mui/material/Rating";
import Box from "@mui/material/Box";
import StarIcon from "@mui/icons-material/Star";
import { ratingLabels } from "../utils/utils";
import { Typography } from "@mui/material";
import { useThemeContext } from "../context/themeContext";

// Used by MUI for accessibility (aria-label text)
function getLabelText(value) {
  return `${value} Star${value !== 1 ? "s" : ""}, ${ratingLabels[value]}`;
}

const maxLabelLength = Math.max(
  ...Object.values(ratingLabels).map((l) => l.length),
);

const SHOW_LABEL_DURATION = 1000;

export default function HoverRating({
  name,
  value, // Controlled rating value from parent (number or null)
  onChange, // Parent handler (event, newValue) => ...
  readOnly = false,
  showLabel = true, // Set false to use the touch-friendly stars without the qualitative label (e.g. a filter threshold, not a content rating).
}) {
  const didMountRef = useRef(false);
  const { isTouchDevice } = useThemeContext();
  // MUI gives us a "hovered/active" value as you move across stars (used for the text label)
  const [hover, setHover] = useState(-1);
  const [showCommittedLabel, setShowCommittedLabel] = useState(false);
  const commitLabelTimeoutRef = useRef(null);

  /**
   * ratingRootRef:
   * - points at the DOM element of the Rating component
   * - we use it to get the element's position/width so we can map touch X coordinate → rating value
   */
  const ratingRootRef = useRef(null);

  /**
   * touchPointerActiveRef:
   * - true while a touch interaction is happening (pointerType !== "mouse")
   * - used to ignore MUI's own onChange while we are handling touch ourselves
   */
  const touchPointerActiveRef = useRef(false);

  /**
   * lastTouchCommitAtRef:
   * - stores the timestamp when *we* committed a value via touch
   * - some browsers fire a synthetic click/change AFTER pointerup; MUI may emit a second onChange then
   * - this helps us ignore those late MUI events briefly to prevent flicker / random null clears
   */
  const lastTouchCommitAtRef = useRef(0);

  /**
   * IGNORE_MUI_ONCHANGE_MS:
   * - how long we ignore MUI's onChange after a touch commit
   * - 400ms is a pragmatic "covers Android synthetic event timing" buffer
   */
  const IGNORE_MUI_ONCHANGE_MS = 400;

  /**
   * clampToHalfStep(raw):
   * - raw is a number like 2.37
   * - we clamp to [0.5, 5] and round to nearest 0.5 (2.5, 3, 3.5, etc)
   * - NOTE: this currently *prevents* 0.0; clearing to null is a separate explicit action (we’ll add later)
   */
  const clampToHalfStep = (raw) => {
    const clamped = Math.min(5, Math.max(0.5, raw));
    return Math.round(clamped * 2) / 2;
  };

  /**
   * computeValueFromPointerEvent(e):
   * - Converts the pointer's X coordinate into a rating value.
   * - Steps:
   *   1) get bounding box of the Rating element (left + width)
   *   2) compute how far across (0 → 1) the pointer is
   *   3) map that ratio across 5 stars
   *   4) clamp/round to nearest 0.5
   *
   * Why do this?
   * - Android sometimes commits the wrong value internally.
   * - But the actual tap coordinate is still correct.
   */
  const computeValueFromPointerEvent = (e) => {
    const el = ratingRootRef.current;
    if (!el) return null;

    const rect = el.getBoundingClientRect();
    const clientX = e.clientX;

    // If we can't read clientX, we can't compute a value
    if (typeof clientX !== "number") return null;

    // X distance from the left edge of the Rating control with bias to account for hitbox mismatch
    const biasPx = rect.width * 0.04;
    const x = clientX - rect.left + biasPx;

    // Convert to a 0..1 ratio across the width
    const ratio = x / rect.width;

    // Map ratio across 5 stars
    const raw = ratio * 5;

    return clampToHalfStep(raw);
  };

  const isSameHalfStep = (a, b) => {
    if (a == null || b == null) return false;
    return Math.abs(a - b) < 0.001;
  };

  const labelValue =
    !isTouchDevice && hover !== -1 // ignore hover value completely for touch
      ? hover
      : showCommittedLabel
        ? value
        : null;

  // set the label to show for one second after the value is updated
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (value == null) return;

    setShowCommittedLabel(true);

    if (commitLabelTimeoutRef.current) {
      clearTimeout(commitLabelTimeoutRef.current);
    }

    commitLabelTimeoutRef.current = setTimeout(() => {
      setShowCommittedLabel(false);
    }, SHOW_LABEL_DURATION);

    return () => {
      if (commitLabelTimeoutRef.current) {
        clearTimeout(commitLabelTimeoutRef.current);
      }
    };
  }, [value]);

  return (
    <Box
      sx={{
        // flex rather than inline-flex: an inline-level box sits on its
        // parent's text baseline, which leaves descender space below it and
        // renders the stars a few pixels above true centre in a fixed-height row.
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        width: "fit-content",
      }}
    >
      <Rating
        name={name}
        // Attach ref so we can measure the element on touch
        ref={ratingRootRef}
        // Controlled value (parent owns it)
        value={value}
        precision={0.5}
        readOnly={readOnly}
        // sx={{ p: "6px 0px" }}
        getLabelText={getLabelText}
        /**
         * MUI's normal commit handler.
         * - On desktop/iOS etc this is fine.
         * - On Android touch, MUI may fire a *wrong* onChange right after our custom commit.
         *   That leads to flicker (value toggling) or random clearing to null.
         *
         * So:
         * - If we're in a touch interaction OR we just committed via touch recently,
         *   ignore MUI's onChange completely.
         */
        onChange={(event, newValue) => {
          const now = Date.now();
          const recentlyCommittedByTouch =
            now - lastTouchCommitAtRef.current < IGNORE_MUI_ONCHANGE_MS;

          if (touchPointerActiveRef.current || recentlyCommittedByTouch) return;

          onChange(event, newValue);
        }}
        /**
         * This is the "active" / hover preview value.
         * - It updates as the user moves over different stars.
         * - We use it ONLY to show the text label (e.g. "very good").
         * - It is NOT the committed value.
         */
        onChangeActive={(event, newHover) => {
          setHover(newHover);
        }}
        emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
        /**
         * pointerdown:
         * - Detect touch pointer (anything except mouse)
         * - Mark that we're handling this interaction ourselves
         * - preventDefault() helps stop Android's synthetic click that can trigger MUI's onChange
         * - setPointerCapture keeps events coming even if the finger drifts a bit
         */
        onPointerDown={(e) => {
          if (readOnly) return;

          if (e.pointerType && e.pointerType !== "mouse") {
            touchPointerActiveRef.current = true;

            e.preventDefault();

            if (e.currentTarget.setPointerCapture && e.pointerId != null) {
              e.currentTarget.setPointerCapture(e.pointerId);
            }
          }
        }}
        /**
         * pointerup:
         * - On touch: compute rating from X position and commit it ourselves.
         * - Record timestamp so we can ignore any late MUI onChange event.
         * - Use setTimeout(0) before clearing the touch flag so we still ignore MUI events in the same tick.
         */
        onPointerUp={(e) => {
          if (readOnly) return;
          if (!(e.pointerType && e.pointerType !== "mouse")) return;

          e.preventDefault();

          const computed = computeValueFromPointerEvent(e);

          if (computed == null) {
            setTimeout(() => {
              touchPointerActiveRef.current = false;
            }, 0);
            return;
          }

          lastTouchCommitAtRef.current = Date.now();

          // If user taps the same value again, clear to null (matches MUI native behaviour)
          const nextValue = isSameHalfStep(value, computed) ? null : computed;

          // Commit OUR computed value (this is the Android fix)
          onChange(e, nextValue);

          setTimeout(() => {
            touchPointerActiveRef.current = false;
          }, 0);
        }}
        /**
         * pointercancel:
         * - If the gesture is cancelled (e.g. browser takes over), ensure we reset our flag.
         */
        onPointerCancel={() => {
          setTimeout(() => {
            touchPointerActiveRef.current = false;
          }, 0);
        }}
      />

      {!readOnly && showLabel && (
        <Typography
          variant="body2"
          sx={{
            ml: 2,
            minWidth: `${maxLabelLength}ch`,
            visibility:
              labelValue != null && labelValue !== -1 ? "visible" : "hidden",
          }}
        >
          {labelValue != null && labelValue !== -1
            ? ratingLabels[labelValue]
            : ""}
        </Typography>
      )}
    </Box>
  );
}
